import React, { Dispatch, SetStateAction } from "react";
import { ChoiceGroup, IComboBoxOption } from "@fluentui/react";
import { CoObjectItem, SheetForm, TgkTableObjectSource } from "../../interfaces/CoObject";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";
import { formatMessageDefault } from "../../../utils/helpers/LanguageHelper";
import { DbApplicationDropDown } from "../DbApplicationDropdown";
import { FileDropDown } from "../FileDropDown";
import { Checkbox } from "@progress/kendo-react-inputs";
import { SourceTypeEnum } from "../../../api/models/source-type-enum";
import useCoObjectSourceTablePanel from "./CoObjectSourceTablePanelHook";
import { DropDownSheet } from "../co-dropdown-panel/drop-down-sheet/DropDownSheet";
import { DropDownAreaAndChart } from "../co-dropdown-panel/drop-down-area-and-chart/DropDownAreaAndChart";
import { FormDropDown } from "../co-dropdown-panel/form-drop-down/FormDropDown";
import { InheritableImageDropdown } from "../InheritableImageDropdown";
import { PasteObject } from "../PasteObject";

export interface CoObjectSourcePanelStateData {
  areasItems?: IComboBoxOption[];
  loading?: boolean;
}

interface CoObjectSourceTableProps {
  objectSource: TgkTableObjectSource;
  updateCoObjectSourceTable: (obj: TgkTableObjectSource, tabValid, modified: boolean) => void;
  isEdit: boolean;
  isLocked: boolean;
  isComponentInstance: boolean;
  tab: ObjectPanelTabs;
  tableType: "TABLE" | "CHART";
  isFileExcelExisting?: boolean;
  fileExcelName?: string;
  updateFileExcelName?: (fileExcelName: string) => void;
  onPasteObject: (smartObject: CoObjectItem) => void;
  originalSheetArea?: SheetForm;
  setOriginalSheetArea?: Dispatch<SetStateAction<SheetForm>>;
  setIsOldSheetOrAreaSelected?: Dispatch<SetStateAction<boolean>>;
  setIsOldValueForAreaToHide?: Dispatch<SetStateAction<boolean>>;
  isOldValueForAreaToHide?: boolean;
  hasSheetToBeSelected?: boolean;
  setHasSheetToBeSelected?: Dispatch<SetStateAction<boolean>>;
  isLoadingDataExcel?: boolean;
  setIsLoadingDataExcel?: Dispatch<SetStateAction<boolean>>;
  setIsLoadingSheetAndArea?: Dispatch<SetStateAction<boolean>>;
  setIsSheetChartAndAreaToHide?: Dispatch<SetStateAction<boolean>>;
  isSheetChartAndAreaToHide?: boolean;
  objectSourceImage?: string;
  isImageUnchecked?: boolean;
}

export const CoObjectSourceTablePanel: React.FunctionComponent<CoObjectSourceTableProps> = (props) => {
  const {
    objectSource,
    tableType,
    sheetsAreaChartMap,
    tableSourceTypeEnumOptions,
    onChangeFormatAs,
    onFormClick,
    selectDbComboBox,
    onFileClick,
    selectSheetComboBox,
    initAreasOrCharts,
    selectAreaComboBox,
    selectChartComboBox,
    onFormatAsImageChange,
    checkboxStyles,
    checkboxLabelStyles,
    onToggleInheritableImageFormat,
    handleChangeImageExtension,
    handleChangeImageResolution,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
    checkboxLabelStylesDisabled,
    isEdit,
    isLocked,
    isComponentInstance,
    isOldValueForAreaToHide,
    setIsOldValueForAreaToHide,
    hasSheetToBeSelected,
    setHasSheetToBeSelected,
    isLoadingDataExcel,
    setIsLoadingDataExcel,
    isImageUnchecked,
  } = useCoObjectSourceTablePanel(props);

  return (
    <>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          {objectSource ? (
            <div>
              {(tableType == "TABLE" || tableType == "CHART") && (
                <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                  <ChoiceGroup
                    disabled={isEdit || isComponentInstance || isLocked || hasSheetToBeSelected}
                    selectedKey={objectSource?.choice}
                    defaultSelectedKey={objectSource?.objectSource}
                    styles={{
                      flexContainer: {
                        display: "flex",
                        justifyContent: "left",
                        columnGap: "0.5rem",
                        flexWrap: "wrap",
                      },
                    }}
                    options={tableSourceTypeEnumOptions}
                    onChange={onChangeFormatAs}
                  />
                </div>
              )}
              {objectSource.objectSource != SourceTypeEnum.EXCEL ? (
                <>
                  <PasteObject onPasteObject={props.onPasteObject} objectType={tableType}></PasteObject>
                  <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                    <DbApplicationDropDown
                      disabled={isComponentInstance || isLocked}
                      dbApplication={objectSource.dbApplication}
                      selectDbComboBox={selectDbComboBox}
                      objectSource={objectSource.objectSource}
                    />
                  </div>
                  <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                    <FormDropDown
                      form={objectSource.form}
                      dbApplication={objectSource.dbApplication}
                      onFormClick={onFormClick}
                      isComponentInstance={isComponentInstance}
                      isLocked={isLocked}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                    <FileDropDown
                      form={objectSource.form}
                      onFileClick={onFileClick}
                      objectSource={objectSource.objectSource}
                      isFileExcelExisting={isFileExcelExisting}
                      fileExcelName={fileExcelName}
                      updateFileExcelName={updateFileExcelName}
                      isComponentInstance={isComponentInstance}
                      isLocked={isLocked}
                    />
                  </div>
                </>
              )}
              <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                <DropDownSheet
                  isEdit={isEdit}
                  sheet={objectSource.sheet}
                  form={objectSource.form}
                  dbApplication={objectSource.dbApplication}
                  selectSheetComboBox={selectSheetComboBox}
                  initAreas={initAreasOrCharts}
                  type={tableType}
                  objectSource={objectSource.objectSource}
                  sheetsAreaChartMap={sheetsAreaChartMap}
                  isFileExcelExisting={isFileExcelExisting}
                  isComponentInstance={isComponentInstance}
                  isLocked={isLocked}
                  originalSheetArea={props.originalSheetArea}
                  setOriginalSheetArea={props.setOriginalSheetArea}
                  setIsOldSheetOrAreaSelected={props.setIsOldSheetOrAreaSelected}
                  setIsOldValueForAreaToHide={setIsOldValueForAreaToHide}
                  setHasSheetToBeSelected={setHasSheetToBeSelected}
                  isLoadingDataExcel={isLoadingDataExcel}
                  setIsLoadingDataExcel={setIsLoadingDataExcel}
                  setIsLoadingSheetAndArea={props.setIsLoadingSheetAndArea}
                  setIsSheetChartAndAreaToHide={props.setIsSheetChartAndAreaToHide}
                  isSheetChartAndAreaToHide={props.isSheetChartAndAreaToHide}
                  objectSourceImage={objectSource?.elabResultType}
                  isImageUnchecked={isImageUnchecked}
                />
              </div>
              <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                {tableType == "TABLE" ? (
                  <DropDownAreaAndChart
                    isEdit={isEdit}
                    name={objectSource.area}
                    sheetsAreaChartMap={sheetsAreaChartMap}
                    sheet={objectSource.sheet}
                    selectNameComboBox={selectAreaComboBox}
                    type={"area"}
                    isFileExcelExisting={isFileExcelExisting}
                    isComponentInstance={isComponentInstance}
                    isLocked={isLocked}
                    objectSource={objectSource.objectSource}
                    isOldValueForAreaToHide={isOldValueForAreaToHide}
                    hasSheetToBeSelected={hasSheetToBeSelected}
                    isLoadingDataExcel={isLoadingDataExcel}
                    setIsOldSheetOrAreaSelected={props.setIsOldSheetOrAreaSelected}
                    setIsSheetChartAndAreaToHide={props.setIsSheetChartAndAreaToHide}
                    isSheetChartAndAreaToHide={props.isSheetChartAndAreaToHide}
                  />
                ) : (
                  <DropDownAreaAndChart
                    isEdit={isEdit}
                    name={objectSource.chart}
                    sheetsAreaChartMap={sheetsAreaChartMap}
                    sheet={objectSource.sheet}
                    selectNameComboBox={selectChartComboBox}
                    type={"chart"}
                    isFileExcelExisting={isFileExcelExisting}
                    isComponentInstance={isComponentInstance}
                    isLocked={isLocked}
                    objectSource={objectSource.objectSource}
                    isOldValueForAreaToHide={isOldValueForAreaToHide}
                    hasSheetToBeSelected={hasSheetToBeSelected}
                    isLoadingDataExcel={isLoadingDataExcel}
                    setIsOldSheetOrAreaSelected={props.setIsOldSheetOrAreaSelected}
                    setIsSheetChartAndAreaToHide={props.setIsSheetChartAndAreaToHide}
                    isSheetChartAndAreaToHide={props.isSheetChartAndAreaToHide}
                  />
                )}
              </div>
              <div style={{ display: "inline-flex" }}>
                <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12" style={{ marginTop: "5px" }}>
                  <Checkbox
                    id="as_image_checkbox"
                    disabled={tableType == "CHART" || !isFileExcelExisting || isComponentInstance || isLocked}
                    checked={objectSource.elabResultType == "IMAGE_STRINGBASE64"}
                    onChange={(event) => onFormatAsImageChange(event)}
                    rounded="small"
                    style={checkboxStyles}
                  />
                  <span style={isFileExcelExisting ? checkboxLabelStyles : checkboxLabelStylesDisabled}>
                    {" "}
                    {formatMessageDefault("cof.createnewobject.insertasimage", "Insert this object as an image")}{" "}
                  </span>
                </div>
              </div>
              {objectSource.elabResultType == "IMAGE_STRINGBASE64" && (
                <>
                  <InheritableImageDropdown
                    type={tableType}
                    inherit={objectSource.inheritFormatImage}
                    imageExtension={objectSource.imageExtension}
                    imageResolution={objectSource.imageResolution}
                    required={true}
                    onToggle={onToggleInheritableImageFormat}
                    onExtensionChange={handleChangeImageExtension}
                    onResolutionChange={handleChangeImageResolution}
                    isFileExcelExisting={isFileExcelExisting}
                    isComponentInstance={isComponentInstance}
                  />
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default CoObjectSourceTablePanel;
